import {
  Box,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material';
import { XIcon } from '@phosphor-icons/react';
import { TagPill, useAppTheme } from '@repo/ui';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { genericMemo, type Props as BaseProps, type Filter, type FilterOption, type FilterValue, type RangeValue, type UseSearchReturn } from '.';

interface Props<T> extends Pick<BaseProps<T>, 'totalCount' | 'pageSize' | 'currentPageSize' | 'dataLabel'> {
  page: number;
  query: string;
  filters: Filter[];
  filterValues: Record<string, FilterValue>;
  sort?: {
    label: string;
    by: string;
    order: 'asc' | 'desc';
  };
  onClearSort: () => void;
  onClearFilter: (dataKey: string, dataValue?: FilterOption) => void;
  onClearQuery: () => void;
  filterPanelShowing: boolean;
  renderTopAction?: (searchState: UseSearchReturn) => React.ReactNode;
  searchState?: UseSearchReturn;
}

interface FilterTag {
  label: string;
  dataKey: string;
  dataValue?: FilterOption;
  isRange?: boolean;
}

const FilterTags = <T,>({
  query,
  totalCount,
  pageSize,
  page,
  currentPageSize,
  sort,
  filters,
  filterValues,
  onClearSort,
  onClearFilter,
  onClearQuery,
  dataLabel,
  renderTopAction,
  searchState,
  filterPanelShowing,
}: Props<T>) => {
  const theme = useAppTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  const containerRef = useRef<HTMLDivElement>(null);
  const rightContentRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState<number>(0);

  // Memoize updateAvailableWidth to prevent recreation on every render
  const updateAvailableWidth = useCallback(() => {
    if (containerRef.current && rightContentRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const rightContentWidth = rightContentRef.current.offsetWidth;
      const spacing = 32; // 4 * 8px (spacing between left and right content)
      const padding = 32; // Some padding for safety
      const available = containerWidth - rightContentWidth - spacing - padding;
      setAvailableWidth(Math.max(available, 200)); // Minimum 200px
    }
  }, []);

  // Optimize useLayoutEffect to be more selective about when it runs
  useLayoutEffect(() => {
    updateAvailableWidth();

    // Only add/remove resize listener if we need to track width changes
    window.addEventListener('resize', updateAvailableWidth);
    return () => window.removeEventListener('resize', updateAvailableWidth);
  }, [updateAvailableWidth]);

  // Separate effect for filterPanelShowing to avoid unnecessary recalculations
  useLayoutEffect(() => {
    // Small delay to ensure layout has settled after panel toggle
    const timeoutId = setTimeout(updateAvailableWidth, 16);
    return () => clearTimeout(timeoutId);
  }, [filterPanelShowing, updateAvailableWidth]);

  // Calculate the start and end indices of displayed courses
  const startIndex = ((page ?? 1) - 1) * pageSize + 1;
  const endIndex = Math.min(startIndex + currentPageSize - 1, totalCount);

  const filterTags = useMemo(() => {
    const tags: FilterTag[] = [];
    for (const filter of filters) {
      const value = filterValues[filter.dataKey];
      if (Array.isArray(value) && value.length > 0) {
        tags.push(...value.map(v => ({ label: `${filter.label}: ${v.label}`, dataKey: filter.dataKey, dataValue: v })));
      } else if (!Array.isArray(value) && value && typeof value === 'object') {
        if ('label' in value) {
          // FilterOption
          tags.push({ label: `${filter.label}: ${value.label}`, dataKey: filter.dataKey, dataValue: value as FilterOption });
        } else if ('min' in value || 'max' in value) {
          // RangeValue
          const rangeValue = value as RangeValue;
          let rangeLabel = '';
          if (rangeValue.min !== undefined && rangeValue.max !== undefined) {
            rangeLabel = `${filter.label}: ${rangeValue.min} - ${rangeValue.max}`;
          } else if (rangeValue.min !== undefined) {
            rangeLabel = `${filter.label}: > ${rangeValue.min}`;
          } else if (rangeValue.max !== undefined) {
            rangeLabel = `${filter.label}: < ${rangeValue.max}`;
          }
          if (rangeLabel) {
            tags.push({ label: rangeLabel, dataKey: filter.dataKey, isRange: true });
          }
        }
      } else if (typeof value === 'boolean' && value === true) {
        tags.push({ label: filter.label, dataKey: filter.dataKey, dataValue: undefined });
      }
    }
    return tags;
  }, [filterValues, filters]);

  // Calculate dynamic max width for tags
  const tagMaxWidth = useMemo(() => {
    if (availableWidth <= 0) return 200; // Default fallback
    return Math.max(150, availableWidth);
  }, [availableWidth]);

  // Memoize text calculation
  const text = useMemo(() => {
    return totalCount === 0 || totalCount === endIndex
      ? `${totalCount} ${dataLabel}${totalCount !== 1 ? 's' : ''}`
      : `Showing ${startIndex} - ${endIndex} of ${totalCount} ${dataLabel}${totalCount !== 1 ? 's' : ''}`;
  }, [totalCount, endIndex, dataLabel, startIndex]);

  // Determine responsive layout
  const totalActiveTags = filterTags.length + (query.length > 0 ? 1 : 0) + (sort ? 1 : 0);
  const isHorizontal = useMemo(() => {
    if (filterPanelShowing) return false;
    if (totalActiveTags === 0 && isMd) return true;
    if (totalActiveTags <= 1 && isLg) return true;
    if (totalActiveTags <= 2 && isLg) return true;
    return false;
  }, [totalActiveTags, isMd, isLg, filterPanelShowing]);

  // Custom clear function for range filters
  const handleClearFilter = useCallback((dataKey: string, dataValue?: FilterOption, isRange?: boolean) => {
    if (isRange) {
      // For range filters, we need to clear the entire range value
      const newFilterValues = {
        ...filterValues,
        [dataKey]: undefined
      };
      searchState?.setFilterValues(newFilterValues);
    } else {
      // Use the existing clear logic for other filter types
      onClearFilter(dataKey, dataValue);
    }
  }, [filterValues, onClearFilter, searchState]);

  return (
    <Stack direction='row' spacing={4} alignItems='flex-start' ref={containerRef}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
          }}
        >
          {query.length > 0 && (
            <TagPill
              text={`Search: ${query}`}
              trailingIcon={<XIcon size={16} />}
              onClick={onClearQuery}
              maxWidth={tagMaxWidth}
              ellipsis={true}
            />
          )}
          {sort && (
            <TagPill
              text={`Sort: ${sort.label}`}
              color="blue"
              trailingIcon={<XIcon size={16} />}
              onClick={onClearSort}
              maxWidth={tagMaxWidth}
              ellipsis={true}
            />
          )}
          {filterTags.map((filter, index) => (
            <TagPill
              key={index}
              text={filter.label}
              color="orange"
              trailingIcon={<XIcon size={16} />}
              onClick={() => handleClearFilter(filter.dataKey, filter.dataValue, filter.isRange)}
              maxWidth={tagMaxWidth}
              ellipsis={true}
            />
          ))}
        </Box>
      </Box>
      <Stack
        direction={isHorizontal ? 'row' : 'column-reverse'}
        spacing={isHorizontal ? 2 : 0.5}
        alignItems={isHorizontal ? 'center' : 'flex-end'}
        sx={{ flexShrink: 0 }}
        ref={rightContentRef}
      >
        <Typography variant="body2">
          {text}
        </Typography>
        {renderTopAction && searchState && (
          <Box>
            {renderTopAction(searchState)}
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default genericMemo(FilterTags);